import {Injectable} from "@angular/core";
import {
  catchError,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap, take,
  tap,
  timer,
  zip
} from "rxjs";
import {NavigationEnd, Router} from "@angular/router";
import {TranslocoService} from "@jsverse/transloco";

import {NetworkService, ConnectionStatus} from "./network.service";
import {JsBridgeService} from "./js-bridge.service";
import {LocalStorageService, StorageProperties} from "./local-storage.service";
import {AuthMode} from "../shared/enums/auth-mode.enum";
import {NavController} from "@ionic/angular/standalone";
import {PathEnum} from "../shared/enums/path-enum";
import {TextSizeService} from "./text-size.service";
import {LanguageService} from "./language.service";
import {TokenExpiryService} from "./token-expiry.service";
import {NotificationService} from "./notification.service";
import {UserSessionService, isProfileForbidden, isCollegamentoRichiesto} from "./user-session.service";
import {CollegamentoAccountService} from "./collegamento-account/collegamento-account.service";
import {OnboardingRouterService} from "./onboarding-router.service";
import {GlobalManagerComuneService} from "./pratiche/global-manager-comune.service";
import {GlobalManagerServizioService} from "./pratiche/global-manager-servizio.service";
import {PushTokenService} from "./push-token.service";
import {PushContentService} from "./push-content.service";
import {environment} from "../../environments/environment";
import {deveSaltareProfiloCittadino} from "../shared/utils/ruoli-token.util";


@Injectable({
  providedIn: 'root'
})
export class AppInitService {

  networkStatus: ConnectionStatus | undefined;

  constructor(
    private router: Router,
    private networkService: NetworkService,
    private jsBridgeService: JsBridgeService,
    private localStorageService: LocalStorageService,
    private userSessionService: UserSessionService,
    private navCtrl: NavController,
    private textSizeService: TextSizeService,
    private languageService: LanguageService,
    private tokenExpiryService: TokenExpiryService,
    private notificationService: NotificationService,
    private onboardingRouter: OnboardingRouterService,
    private transloco: TranslocoService,
    private globalManagerComuneService: GlobalManagerComuneService,
    private globalManagerServizioService: GlobalManagerServizioService,
    private pushTokenService: PushTokenService,
    private pushContentService: PushContentService,
    // Opzionale nel tipo solo per non rompere chi costruisce il servizio a mano (spec esistenti):
    // in app Angular lo inietta sempre.
    private collegamentoAccount?: CollegamentoAccountService) {
  }

  init() {
    console.log('[AppInit] init() START');

    return new Observable((subscriber) => {

      // Wait for native bridge ready, then check sessionStorage for session recovery.
      this.jsBridgeService.getSharedValues([]);

      from(this.jsBridgeService.valuesReady).pipe(

        tap((v) => {
          // Mai loggare il payload (contiene i token). Instrada le chiavi auth
          // verso sessionStorage via LocalStorageService; le non sensibili sono
          // già gestite dal bridge (allow-list).
          this.applyInboundAuthValues(v);
        }),

        switchMap(() => from(this.languageService.init())),

        switchMap(() => from(this.textSizeService.init())),

        switchMap(() =>
          zip([
            this.init$(),
            timer(3000)
          ]).pipe(
            map(value => value[0]),
          )
        ),

        tap(() => {
          console.log('[AppInit] NavigationEnd listener setup');
          const sub = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            take(1),
          ).subscribe(() => {
            console.log('[AppInit] NavigationEnd fired → appInitEnd()');
            sub.unsubscribe();
            this.jsBridgeService.appInitEnd();
          });
        }),

      ).subscribe({
        next: value => {
          console.log('[AppInit] init() COMPLETED', value);
          subscriber.complete();
        },
        error: err => {
          console.error('[AppInit] init() ERROR', err);
          subscriber.error(err);
        }
      });
    });
  }


  /**
   * Instrada le chiavi auth ricevute dalla shell nativa verso sessionStorage
   * (via LocalStorageService), così non restano in localStorage. Le chiavi non
   * sensibili sono già gestite dal bridge tramite allow-list. Nessun valore
   * viene loggato.
   */
  private applyInboundAuthValues(values: unknown): void {
    if (!values || typeof values !== 'object') {
      return;
    }
    const v = values as Record<string, unknown>;

    const accessToken = v[StorageProperties.ACCESS_TOKEN];
    if (typeof accessToken === 'string' && accessToken) {
      this.localStorageService.setAccessToken$(accessToken);
    }

    const refreshToken = v[StorageProperties.REFRESH_TOKEN];
    if (typeof refreshToken === 'string' && refreshToken) {
      this.localStorageService.setRefreshToken$(refreshToken);
    }

    const authMode = v[StorageProperties.AUTH_MODE];
    if (typeof authMode === 'string' && authMode) {
      this.localStorageService.setAuthMode$(authMode as AuthMode);
    }

    // Sessione SPID ripristinata a freddo dalla shell nativa: senza questo instradamento
    // spidAccessToken resterebbe null per un utente SPID già autenticato (regressione post ed0bb71).
    const spidAccessToken = v[StorageProperties.SPID_ACCESS_TOKEN];
    if (typeof spidAccessToken === 'string' && spidAccessToken) {
      this.localStorageService.setSpidAccessToken$(spidAccessToken);
    }
  }

  async getNetWorkStatus() {
    this.networkStatus = {connected: navigator.onLine, connectionType: navigator.onLine ? 'wifi' : 'none'};
    console.log('[Network] status:', this.networkStatus);
    return !this.networkStatus?.connected;
  }


  init$() {
    return from(this.getNetWorkStatus()).pipe(
      tap((isOffline) =>
        this.networkService.sendStatusChangeMsg(
          {
            connected: !isOffline,
            connectionType: 'unknown'
          })
      ),
      switchMap((isOffline) => this.initAfterCheckNetwork$(isOffline)),
    )
  }

  initAfterCheckNetwork$(isOffline: boolean) {
    // Skip init per callback SPID (verrà gestito dal CallbackComponent)
    if (this.isSpidCallbackPath()) {
      return of(null);
    }

    // Identità SPID/CIE in attesa di collegamento (reload della pagina con la modale aperta):
    // si ripresenta la modale senza bloccare l'avvio. Non c'è sessione standard, quindi si prosegue
    // verso il login; la modale compare dopo il primo NavigationEnd.
    if (!this.localStorageService.getAccessToken() && this.collegamentoAccount?.haSlotPending()) {
      this.collegamentoAccount!.riprendi$().subscribe();
      return of(null);
    }

    // Se ci sono token in sessionStorage (ripristinati nel costruttore di LocalStorageService),
    // tenta il recovery della sessione: ricarica profilo, anagrafiche e comune.
    if (this.localStorageService.getAccessToken()) {
      console.log('[AppInit] Token found in sessionStorage, attempting session recovery');
      this.tokenExpiryService.startMonitoring(this.localStorageService.getAccessToken()!);
      return this.recoverSession$();
    }

    // Nessun token → login page
    return of(null);
  }

  private recoverSession$(): Observable<any> {
    return this.fetchInitialData$().pipe(
      switchMap(({profile}) => {
        if (profile?.id) {
          this.jsBridgeService.login({accessToken: this.localStorageService.getAccessToken()!, userId: profile.id});
          // Sessione ripristinata: se il guscio nativo ha già consegnato il push token (arriva su
          // `appInitEnd`, quindi può precedere questo punto) lo registra ora che c'è un Bearer valido.
          this.pushTokenService.registraTokenPendente$().subscribe();
        }
        return this.restoreComuneAttivo$().pipe(map(() => profile));
      }),
      switchMap((profile) => {
        const browserPath = location.pathname + location.search;
        const hasExplicitRoute = (environment as any).respectBrowserUrl
          && browserPath !== '/'
          && browserPath !== '';

        if (hasExplicitRoute) {
          console.log('[AppInit] Respecting browser URL:', browserPath);
          this.navCtrl.navigateRoot(browserPath);
          return of(profile);
        }

        return this.onboardingRouter.computeNextRoute().pipe(
          tap((route) => {
            console.log('[AppInit] Session recovered, routing to', route);
            this.navCtrl.navigateRoot(route);
          }),
        );
      }),
      // Notifica push messa in attesa in un ciclo di vita precedente della web app (payload
      // arrivato senza sessione, poi reload con token ancora in sessionStorage): ora la sessione è
      // di nuovo attiva, quindi va mostrata. Nel caso più comune non c'è nulla in attesa e questo è
      // un no-op: al bootstrap il nativo consegna la push DOPO `appInitEnd`, quindi il recovery è
      // già concluso e `gestisciNotifichePush` trova la sessione viva e apre la modale da sé.
      tap(() => {
        this.pushContentService.mostraNotificaInAttesa()
          .catch((e) => console.warn('[AppInit] presentazione notifica push in attesa non riuscita', e));
      }),
      catchError((err) => {
        // 409 collegamento_account_richiesto (token ripristinato dalla shell nativa di un'identità
        // da collegare): stessa gestione dell'auth-callback. Non blocca l'avvio.
        if (isCollegamentoRichiesto(err)) {
          this.collegamentoAccount?.avviaDaSessioneCorrente()
            .catch((e) => console.error('[AppInit] avvio collegamento non riuscito', e));
          return of(null);
        }
        if (err?.status === 401) {
          return of(null);
        }
        // 403 sul GET profilo: il token è valido ma l'utente non è autorizzato ad accedere.
        // La sessione è già stata chiusa da UserSessionService.refreshUserData$(): qui
        // resta solo il toast di feedback.
        if (isProfileForbidden(err)) {
          console.warn('[AppInit] Access denied on initial profile (403), session already closed');
          this.notificationService.openToast({
            message: this.transloco.translate('common.toast.access_denied_login'),
            color: 'warning'
          });
          return of(null);
        }
        console.error('[AppInit] Session recovery failed after retry', err);
        return this.localStorageService.cleanPreferenceToken$().pipe(
          tap(() => {
            this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true});
            this.notificationService.openToast({
              message: this.transloco.translate('common.toast.session_reload_failed'),
              color: 'warning'
            });
          }),
          map(() => null),
        );
      }),
    );
  }

  private fetchInitialData$(): Observable<{profile: any}> {
    return this.userSessionService.refreshUserData$().pipe(
      map(({profile}) => ({profile})),
    );
  }

  private restoreComuneAttivo$(): Observable<void> {
    // Operatore di backoffice: non ha un profilo cittadino, quindi nemmeno un "comune attivo" nel
    // senso del cittadino. La risoluzione del servizio sotto chiama GET /cittadino/servizi, che
    // per lui e' 403 certo: una richiesta che si sa negata, registrata come accesso rifiutato nei
    // log. Si salta l'intero ripristino invece di assorbirne l'errore — stesso criterio con cui
    // UserSessionService.refreshUserData$() salta la GET del profilo cittadino.
    // Il predicato vale solo per chi NON e' anche cittadino: il doppio ruolo resta un cittadino.
    const identity = this.localStorageService.decodeClaims();
    if (deveSaltareProfiloCittadino(identity?.roles)) {
      return of(undefined);
    }

    return this.localStorageService.getComuneAttivo$().pipe(
      tap((comune) => {
        if (comune) {
          console.log('[AppInit] Restoring Comune attivo from storage:', comune.slug);
          this.globalManagerComuneService.setComuneSelezionato(comune);
        }
      }),
      switchMap(() => this.localStorageService.getSlugComune$()),
      switchMap((slugComune) => {
        if (!slugComune) {
          return of(undefined);
        }
        // Ripristino/auto-selezione del servizio attivo (storage → ultimo usato).
        return this.globalManagerServizioService
          .risolviServizioAttivo$(slugComune, null)
          .pipe(
            tap((servizio) =>
              console.log('[AppInit] Servizio attivo risolto:', servizio?.slug ?? '(nessuno)')
            ),
            map(() => undefined),
            catchError(() => of(undefined)),
          );
      }),
      map(() => undefined),
    );
  }

  private isSpidCallbackPath(): boolean {
    return location.pathname.includes('/auth/callback');
  }


}
